import { Button, Popconfirm, Table } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { CheckCircleTwoTone, DeleteOutlined } from './Icons';
import { IMembershipCardData } from './MembershipCardModal';
import { loadStripe } from '@stripe/stripe-js';
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import memberCardKeyService from '@/services/memberCardKeyService';
import { IMemberCardKey } from '@/types';
import Modal from 'antd/lib/modal/Modal';
import Brand from './Brand';

interface ITableColumns {
  title: string;
  key?: string;
  align?: string;
  dataIndex?: string | string[];
  render: Function;
}

let stripePromise = null;

const load_stripe = (pkStripe) => {
  stripePromise = loadStripe(pkStripe);
};

const StripeCard = ({ ui, uiSet, reloadCards }) => {
  const [errors, setErrors] = useState<string>('');
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const elements = useElements();
  const stripe = useStripe();
  const add_credit_card = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    const cardElement = elements.getElement(CardElement);
    setConfirmLoading(true);
    const result = await stripe.createToken(cardElement);
    if (result.error) {
      setConfirmLoading(false);
      setErrors(result.error.message);
    } else {
      const ret = await memberCardKeyService.AddCard({
        payment_key: result?.token?.id,
        payment_card: result?.token?.card?.last4,
        payment_card_brand: result?.token?.card?.brand,
        payment_card_country: result?.token?.card?.country,
        payment_card_zip: result?.token?.card?.address_zip,
        exp_month: result?.token?.card?.exp_month,
        exp_year: result?.token?.card?.exp_year,
      } as IMemberCardKey);
      if (ret.status === 'OK') {
        cardElement.clear();
        uiSet({ ...ui, showCard: false });
        reloadCards();
      } else {
        setConfirmLoading(false);
        setErrors(ret.error);
      }
      setConfirmLoading(false);
    }
  };

  return (
    <Modal
      visible={ui.showCard}
      title="Add Credit Card"
      onOk={add_credit_card}
      closable={!confirmLoading}
      keyboard={false}
      onCancel={() => {
        if (confirmLoading) return false;
        uiSet({ ...ui, showCard: false });
      }}
      confirmLoading={confirmLoading}
    >
      <CardElement
        className={`border ${
          ui.stripeErrors ? 'border-red-500' : ''
        } rounded-md pl-4 py-4`}
        options={{
          style: {
            base: {
              fontSize: '16px',
              color: '#424770',
              '::placeholder': {
                color: '#aab7c4',
              },
            },
            invalid: {
              color: '#9e2146',
            },
          },
        }}
      />
      {errors && <span>Errors: {errors}</span>}
    </Modal>
  );
};

export default function MemberCardKeys({ user, configInfo }) {
  const [ui, uiSet] = useState<IMembershipCardData>({
    showCard: false,
  } as IMembershipCardData);
  const [keys, setKeys] = useState([] as Array<IMemberCardKey>);
  useEffect(() => {
    reloadCards();
    if (configInfo.pkStripe) {
      load_stripe(configInfo.pkStripe);
    }
  }, [configInfo.pkStripe]);
  const reloadCards = () => {
    memberCardKeyService.GetMemberKeyCards().then((resp) => {
      setKeys(resp);
    });
  };

  const do_delete = async (id) => {
    await memberCardKeyService.DeleteCard(id);
    reloadCards();
  };

  const columns: ITableColumns[] = [
    {
      title: 'Brand',
      align: 'left',
      dataIndex: 'payment_card_brand',
      render: (_text, record) => <Brand type={record.payment_card_brand} />,
    },
    {
      title: 'Card',
      align: 'left',
      dataIndex: 'payment_card',
      render: (_text, record) => `**** **** **** ${record.payment_card}`,
    },
    {
      title: 'Expire',
      align: 'left',
      dataIndex: ['exp_year', 'exp_month'],
      render: (_text, record: IMemberCardKey) =>
        `${record?.exp_month}/${record?.exp_year}`,
    },
    {
      title: 'Address',
      align: 'left',
      dataIndex: ['payment_card_country', 'payment_card_zip'],
      render: (text, record: IMemberCardKey) =>
        `${record?.payment_card_country}${
          record?.payment_card_zip ? ' - ' + record?.payment_card_zip : ''
        }`,
    },
    {
      title: 'Action',
      key: 'action',
      align: 'center',
      render: (text, record: IMemberCardKey) => (
        <Popconfirm
          title="Are you sure？"
          okText="Yes"
          cancelText="No"
          onConfirm={async () => await do_delete(record.member_cardkey_id)}
        >
          <a
            href="#"
            title="Delete this card"
            className="text-center mx-auto w-full"
          >
            <DeleteOutlined />
          </a>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div className="border border-primary rounded-md overflow-hidden mb-8">
      <div className="text-sm text-primary bg-orange-50 border-b border-primary  flex flex-row items-center w-full relative p-3">
        <span className="ml-2">Saved Credit Cards</span>
        {dayjs(user?.member_dt_next_payment) > dayjs() && (
          <CheckCircleTwoTone
            className="absolute right-4 text-2xl top-2.5"
            twoToneColor="#52c41a"
          />
        )}
      </div>
      <Table
        columns={columns as []}
        dataSource={keys}
        pagination={{ style: { padding: '0 20px' } }}
      />
      <div className="p-4">
        <Button
          type="primary"
          className="uppercase"
          onClick={() => uiSet({ ...ui, showCard: true })}
        >
          Add Card (No charges)
        </Button>
        <Elements stripe={stripePromise}>
          <StripeCard ui={ui} uiSet={uiSet} reloadCards={reloadCards} />
        </Elements>
      </div>
    </div>
  );
}
